<script setup>
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useToast } from 'vue-toastification'
import Shared from '@/common/shared'
import moment from 'moment'
import { Dictionary, FullAssetVersion, MySelf, Regions } from '@/common/api.service'
import { isVersionFieldChanged, isVersionTotalFieldChanged, normalizeVersionDifferences } from '@/components/Capitalization/versionDifferences'

const route = useRoute()
const router = useRouter()
const toast = useToast()
const list = ref([])
const years = ref([])
const currentYear = new Date().getFullYear()
const filterYear = ref(route.query.year ? parseYear(route.query.year) : currentYear)
const filterVersion = ref(route.query.version ? String(route.query.version) : '')
const filterRegion = ref(parseRegionId(route.params.id))
const selectedYear = ref(filterYear.value)
const versions = ref([])
const versionsLoaded = ref(false)
const selectedVersion = ref(filterVersion.value)
const loaded = ref(false)
const isInitialized = ref(false)
const saving = ref(false)
const forbidden = ref(false)
const versionHistory = ref([])
const historyLoading = ref(false)
const versionDifferences = ref(new Set())
const versionData = ref({
  capitalization: { rows: [], total: {}, year: null },
  budget: { years: [], requestedYear: null },
})
const capitalizationRows = ref([])
const budgetRows = ref([])
const permissions = ref([])
const isRegionalFundEmployee = ref(false)
const idRegionalFundEmployee = ref(0)
const regionActive = ref(null)

const VERSION_STATUS_EDITABLE_MAX = 1
const VERSION_STATUS_REJECTED = 4

const VERSION_STATUS_NAMES = {
  0: 'Черновик',
  1: 'Черновик',
  2: 'Направлена на рассмотрение',
  3: 'Согласована',
  4: 'Отклонена',
}

function permission(permissionName) {
  if (!permissions.value.find((p) => p === permissionName)) {
    console.warn('no permission', permissionName)
  }
  return permissions.value.find((p) => p === permissionName)
}

function parseRegionId(id) {
  if (!id) return ''
  const num = Number(id)
  return Number.isNaN(num) ? id : num
}

function parseYear(year) {
  if (!year) return ''
  const num = Number(year)
  return Number.isNaN(num) ? year : num
}

function getDefaultYear() {
  const defaultYear = years.value.find(year => year.value === currentYear)
  if (defaultYear) return defaultYear.value
  if (years.value.length > 0) return years.value[0].value
  return currentYear
}

function capitalizationViewRoute(regionId, year, version) {
  const query = {}
  if (year) {
    query.year = year
  }
  if (version) {
    query.version = version
  }
  return { name: 'CapitalizationView', params: { id: regionId }, query }
}

function capitalizationEditRoute(regionId, year, version) {
  const query = {}
  if (year) {
    query.year = year
  }
  if (version) {
    query.version = version
  }
  return { name: 'CapitalizationEdit', params: { id: regionId }, query }
}

function syncFiltersFromRoute() {
  filterRegion.value = parseRegionId(route.params.id)
  filterYear.value = route.query.year ? parseYear(route.query.year) : filterYear.value
  filterVersion.value = route.query.version ? String(route.query.version) : ''
  selectedRegion.value = filterRegion.value
  selectedYear.value = filterYear.value
  selectedVersion.value = filterVersion.value
}

function isEditRouteActive() {
  return route.name === 'CapitalizationEdit'
}

async function applyFilters() {
  if (!isEditRouteActive()) return

  const nextRegion = filterRegion.value
  const nextYear = filterYear.value
  const nextVersion = filterVersion.value ? String(filterVersion.value) : ''
  const regionChanged = String(nextRegion) !== String(route.params.id)
  const queryChanged = String(nextYear) !== String(route.query.year || '')
    || String(nextVersion) !== String(route.query.version || '')

  selectedRegion.value = nextRegion
  selectedYear.value = nextYear
  selectedVersion.value = nextVersion

  if (regionChanged) {
    await router.push(capitalizationEditRoute(nextRegion, nextYear, nextVersion))
    return
  }
  if (queryChanged) {
    await router.replace(capitalizationEditRoute(route.params.id, nextYear, nextVersion))
    return
  }
  await fetchVersions()
}

const selectedRegion = ref(filterRegion.value)

function parseNumber(value) {
  if (value === '' || value == null) return null
  if (typeof value === 'number') {
    return Number.isNaN(value) ? null : value
  }
  const normalized = String(value).replace(/\s/g, '').replace(',', '.')
  const num = parseFloat(normalized)
  return Number.isNaN(num) ? null : num
}

function rowLevel(row) {
  if (row.level == null || row.level === '') return null
  const level = Number(row.level)
  return Number.isNaN(level) ? null : level
}

function getTopLevel(rows) {
  const levels = rows.map(row => rowLevel(row)).filter(level => level != null)
  if (levels.length === 0) return null
  return Math.min(...levels)
}

function isRowEditable(rows, index) {
  const row = rows[index]
  if (row?.editable === false) return false

  const level = rowLevel(row)
  if (level == null) return true

  for (let i = index + 1; i < rows.length; i++) {
    const nextLevel = rowLevel(rows[i])
    if (nextLevel == null) continue
    if (nextLevel <= level) break
    return false
  }

  return true
}

function isCapitalizationRowEditable(index) {
  return isRowEditable(capitalizationRows.value, index)
}

function capitalizationRowQuarterDisplay(index, quarter) {
  return effectiveQuarterValue(capitalizationRows.value, index, quarter)
}

function effectiveQuarterValue(rows, index, quarter) {
  const row = rows[index]
  const level = rowLevel(row)
  const childIndices = []
  for (let i = index + 1; i < rows.length; i++) {
    const childLevel = rowLevel(rows[i])
    if (childLevel == null || level == null || childLevel <= level) break
    if (childLevel === level + 1) {
      childIndices.push(i)
    }
  }
  if (childIndices.length === 0) {
    return parseNumber(row[quarter]) ?? 0
  }
  return childIndices.reduce((sum, i) => sum + effectiveQuarterValue(rows, i, quarter), 0)
}

function sumCapitalizationQuarter(quarter) {
  const rows = capitalizationRows.value
  const topLevel = getTopLevel(rows)
  if (topLevel == null) return 0
  return rows.reduce((sum, row, index) => {
    if (rowLevel(row) !== topLevel) return sum
    return sum + effectiveQuarterValue(rows, index, quarter)
  }, 0)
}

function applyEditableRows(data) {
  capitalizationRows.value = (data.capitalization?.rows ?? []).map(row => ({
    code: row.code,
    title: row.title,
    level: row.level,
    editable: row.editable,
    quarter1: row.quarter1,
    quarter2: row.quarter2,
    quarter3: row.quarter3,
    quarter4: row.quarter4,
  }))

  const budgetYears = data.budget?.years ?? []
  const yearData = budgetYears.find(year => year.year === Number(selectedYear.value)) ?? budgetYears[0] ?? { rows: [] }
  budgetRows.value = (yearData.rows ?? []).map(row => ({
    code: row.code,
    title: row.title,
    value: row.value,
  }))
}

async function denyAccess() {
  forbidden.value = true
  await router.replace({ name: 'ForbiddenPage' })
}

async function validateEditAccess() {
  if (!permission('/funds/asset/versions:update')) {
    await denyAccess()
    return false
  }

  const status = versionData.value.versionStatus
  if (status === VERSION_STATUS_REJECTED) {
    await router.replace(capitalizationViewRoute(selectedRegion.value, selectedYear.value, selectedVersion.value))
    return false
  }

  if (isRegionalFundEmployee.value) {
    if (status == null || status > VERSION_STATUS_EDITABLE_MAX) {
      await router.replace(capitalizationViewRoute(selectedRegion.value, selectedYear.value, selectedVersion.value))
      return false
    }
  }

  return true
}

const yearsList = computed(() => {
  const r = []
  years.value.forEach(function (year) {
    r.push({ value: year.value, label: year.title })
  })
  return r
})

const versionsList = computed(() => {
  return versions.value.map(function (version) {
    return {
      value: version.versionId,
      label: version.versionCode ? `Версия ${version.versionCode}` : `Версия ${version.versionId}`,
    }
  })
})

const selectedFundId = computed(() => {
  const region = list.value.find(r => String(r.id) === String(selectedRegion.value))
  return region?.fund?.id ?? ''
})

const filterFundId = computed(() => {
  const region = list.value.find(r => String(r.id) === String(filterRegion.value))
  return region?.fund?.id ?? ''
})

const regionList = computed(() => {
  const r = []
  list.value.forEach(function (f) {
    r.push({ value: f.id, label: `${f.title}` })
  })
  return r
})

const selectedRegionTitle = computed(() => {
  const region = list.value.find(r => String(r.id) === String(selectedRegion.value))
  return region ? region.title : ''
})

const capitalizationTotal = computed(() => {
  const apiTotal = versionData.value.capitalization?.total ?? {}
  return {
    code: apiTotal.code,
    title: apiTotal.title,
    quarter1: sumCapitalizationQuarter('quarter1'),
    quarter2: sumCapitalizationQuarter('quarter2'),
    quarter3: sumCapitalizationQuarter('quarter3'),
    quarter4: sumCapitalizationQuarter('quarter4'),
  }
})

const budgetTotal = computed(() => {
  const budgetYears = versionData.value.budget?.years ?? []
  const yearData = budgetYears.find(year => year.year === Number(selectedYear.value)) ?? budgetYears[0] ?? { total: {} }
  const apiTotal = yearData.total ?? {}
  const value = budgetRows.value.reduce((sum, row) => sum + (parseNumber(row.value) ?? 0), 0)
  return {
    title: apiTotal.title,
    code: apiTotal.code,
    value,
  }
})

const versionStatusText = computed(() => {
  if (versionData.value.versionStatusName) {
    return versionData.value.versionStatusName
  }
  if (versionData.value.versionStatus === 0 || versionData.value.versionStatus === 1) {
    return 'Черновик'
  }
  return ''
})

const emptyStateMessage = computed(() => {
  if (versions.value.length === 0) {
    return 'По данному году версий не создано'
  }
  return 'Выберите версию для редактирования'
})

function historyStatusName(status) {
  return VERSION_STATUS_NAMES[status] ?? ''
}

function isFieldChanged(code, field) {
  return isVersionFieldChanged(versionDifferences.value, code, field)
}

function capitalizationInputClass(code, field) {
  return {
    field: true,
    'cap-input': true,
    'cap-value-changed': isFieldChanged(code, field),
  }
}

function budgetInputClass(code) {
  return capitalizationInputClass(code, 'value')
}

function formatDisplayNumber(value) {
  if (value === null || value === undefined || value === '') {
    return ''
  }
  const val = parseFloat(value).toFixed(2)
  return `${val}`.replace(/(\d)(?=(\d{3})+([^\d]|$))/g, '$1 ')
}

function readonlyValueClass(code, field) {
  return {
    'cap-readonly-value': true,
    'cap-value-changed': isFieldChanged(code, field),
  }
}

function readonlyTotalValueClass(code, field) {
  return {
    'cap-readonly-value': true,
    'cap-value-changed': isVersionTotalFieldChanged(versionDifferences.value, code, field),
  }
}

async function showVersionHistory() {
  const fundId = selectedFundId.value
  const year = selectedYear.value
  const versionId = selectedVersion.value
  if (!fundId || !year || !versionId || historyLoading.value) {
    return
  }

  historyLoading.value = true
  versionHistory.value = []
  try {
    const { data } = await FullAssetVersion.history(fundId, year, versionId)
    versionHistory.value = data
    Shared.showModal('#version-history')
  } catch {
    versionHistory.value = []
  } finally {
    historyLoading.value = false
  }
}

async function fetchFilterVersions() {
  const fundId = filterFundId.value
  const year = filterYear.value
  if (!fundId || !year) {
    versions.value = []
    return
  }

  try {
    const { data } = await FullAssetVersion.list(fundId, year)
    versions.value = data
    if (filterVersion.value && !versions.value.find(version => String(version.versionId) === String(filterVersion.value))) {
      filterVersion.value = ''
    }
  } catch {
    versions.value = []
    filterVersion.value = ''
  }
}

async function fetchVersions() {
  const fundId = selectedFundId.value
  const year = selectedYear.value
  versionsLoaded.value = false
  if (!fundId || !year) {
    versions.value = []
    selectedVersion.value = ''
    versionsLoaded.value = true
    await fetchVersionData()
    return
  }

  await FullAssetVersion.list(fundId, year)
      .then(({ data }) => {
        versions.value = data
        if (selectedVersion.value && !versions.value.find(version => String(version.versionId) === String(selectedVersion.value))) {
          selectedVersion.value = ''
          filterVersion.value = ''
        }
      })
      .catch(() => {
        versions.value = []
        selectedVersion.value = ''
      })
      .finally(() => {
        versionsLoaded.value = true
      })

  await fetchVersionData()
}

async function fetchVersionDifferences() {
  const fundId = selectedFundId.value
  const year = selectedYear.value
  const versionId = selectedVersion.value
  versionDifferences.value = new Set()
  if (!fundId || !year || !versionId) {
    return
  }

  try {
    const { data } = await FullAssetVersion.differences(fundId, year, versionId)
    versionDifferences.value = normalizeVersionDifferences(data)
  } catch {
    versionDifferences.value = new Set()
  }
}

async function fetchVersionData() {
  const fundId = selectedFundId.value
  const year = selectedYear.value
  const versionId = selectedVersion.value
  versionDifferences.value = new Set()
  if (!fundId || !year || !versionId) {
    versionData.value = {
      capitalization: { rows: [], total: {}, year: null },
      budget: { years: [], requestedYear: null },
    }
    capitalizationRows.value = []
    budgetRows.value = []
    loaded.value = false
    return
  }

  loaded.value = false
  try {
    const [{ data }] = await Promise.all([
      FullAssetVersion.item(fundId, year, versionId),
      fetchVersionDifferences(),
    ])
    versionData.value = data
    applyEditableRows(data)
    if (await validateEditAccess()) {
      loaded.value = true
    }
  } catch {
    versionData.value = {
      capitalization: { rows: [], total: {}, year: null },
      budget: { years: [], requestedYear: null },
    }
    capitalizationRows.value = []
    budgetRows.value = []
    loaded.value = false
    versionDifferences.value = new Set()
    await denyAccess()
  }
}

function cancelEdit() {
  router.push(capitalizationViewRoute(selectedRegion.value, selectedYear.value, selectedVersion.value))
}

async function saveVersion() {
  const fundId = selectedFundId.value
  const year = selectedYear.value
  const versionId = selectedVersion.value
  if (!fundId || !year || !versionId || saving.value) {
    return
  }

  saving.value = true
  try {
    await FullAssetVersion.update(fundId, year, versionId, {
      capitalizationRows: capitalizationRows.value
          .filter((row, index) => isRowEditable(capitalizationRows.value, index))
          .map(row => ({
            code: row.code,
            quarter1: parseNumber(row.quarter1),
            quarter2: parseNumber(row.quarter2),
            quarter3: parseNumber(row.quarter3),
            quarter4: parseNumber(row.quarter4),
          })),
      budgetRows: budgetRows.value.map(row => ({
        code: row.code,
        value: parseNumber(row.value) ?? 0,
      })),
    })
    router.push(capitalizationViewRoute(selectedRegion.value, selectedYear.value, selectedVersion.value))
  } catch (error) {
    if (error.response?.status === 400) {
      toast.error(error.response.data?.title || 'Не удалось сохранить данные', {
        timeout: 5000,
      })
    }
  } finally {
    saving.value = false
  }
}

watch(filterYear, async (year, oldYear) => {
  const changed = oldYear !== undefined && String(year) !== String(oldYear)
  if (changed) {
    const hadVersion = !!filterVersion.value
    filterVersion.value = ''
    if (isInitialized.value) {
      await fetchFilterVersions()
      if (!hadVersion) {
        await applyFilters()
      }
    }
    return
  }
  if (isInitialized.value) {
    await fetchFilterVersions()
  }
})

watch(filterRegion, async (region, oldRegion) => {
  const changed = oldRegion !== undefined && String(region) !== String(oldRegion)
  if (changed) {
    const hadVersion = !!filterVersion.value
    filterVersion.value = ''
    if (isInitialized.value) {
      await fetchFilterVersions()
      if (!hadVersion) {
        await applyFilters()
      }
    }
    return
  }
  if (isInitialized.value) {
    await fetchFilterVersions()
  }
})

watch(filterVersion, async (version, oldVersion) => {
  if (!isInitialized.value || oldVersion === undefined) return
  if (String(version) === String(oldVersion)) return
  await applyFilters()
})

watch(() => [route.params.id, route.query.year, route.query.version], async () => {
  if (!isInitialized.value || !isEditRouteActive()) return
  syncFiltersFromRoute()
  await fetchVersions()
})

await Regions.permissions()
    .then(({ data }) => {
      permissions.value = data
    })
    .catch(() => {
    })

if (!permission('/funds/asset/versions:update')) {
  await denyAccess()
} else {
  await Dictionary.fundCapitalizationYears()
      .then(({ data }) => {
        years.value = data
        if (route.query.year) {
          filterYear.value = parseYear(route.query.year)
          selectedYear.value = filterYear.value
        } else {
          const year = getDefaultYear()
          filterYear.value = year
          selectedYear.value = year
          router.replace(capitalizationEditRoute(route.params.id, year, route.query.version))
        }
      })
      .catch(() => {
      })

  await MySelf.card().then(({ data }) => {
    if (data.isRegionalFundEmployee) {
      isRegionalFundEmployee.value = true
      idRegionalFundEmployee.value = data.relatedRegionId
    }
  })

  if (!isRegionalFundEmployee.value) {
    await Regions.list({})
        .then(async ({ data }) => {
          list.value = data
          syncFiltersFromRoute()
          isInitialized.value = true
          await fetchVersions()
        })
        .catch(() => {
        })
  } else {
    await Regions.item(idRegionalFundEmployee.value)
        .then(async ({ data }) => {
          list.value = [data]
          regionActive.value = data
          const regionId = parseRegionId(data.id)
          filterRegion.value = regionId
          selectedRegion.value = regionId
          if (String(route.params.id) !== String(regionId)) {
            router.replace(capitalizationEditRoute(regionId, selectedYear.value, route.query.version))
          }
          syncFiltersFromRoute()
          isInitialized.value = true
          await fetchVersions()
        })
        .catch(() => {
        })
  }
}
</script>

<template>
  <div class="wrapper" v-if="!forbidden">
    <MainHeader></MainHeader>
    <main class="page page--js">
      <div class="breadcrumbs-container">
        <div class="container">
          <div class="breadcrumbs-wrapper cap-breadcrumbs">
            <ul class="breadcrumbs">
              <li>Модуль регионов</li>
              <li v-if="!isRegionalFundEmployee">
                <router-link :to="{ name: 'ListRegions' }">Список регионов</router-link>
              </li>
              <li v-else>Список регионов</li>
              <li v-if="isRegionalFundEmployee && regionActive">
                <router-link :to="{ name: 'CardRegions', params: { id: regionActive.id } }">{{ regionActive.title }}</router-link>
              </li>
              <li v-else-if="selectedRegionTitle">
                <router-link :to="{ name: 'CardRegions', params: { id: selectedRegion } }">{{ selectedRegionTitle }}</router-link>
              </li>
              <li>Капитализация РФРП</li>
            </ul>
          </div>
        </div>
      </div>

      <div class="filters-container cap-filters-container">
        <div class="container">
          <div class="filters cap-filters">
            <div class="filters__body cap-filters__body">
              <Multiselect class="field field--select field--m-width-265 margin-0" v-model="filterYear"
                           mode="single" :options="yearsList" :show-no-results="false" :searchable="true"
                           placeholder="Год" :no-options-text="'Укажите год'" />
              <Multiselect class="field field--select field--m-width-265 margin-0" v-model="filterVersion"
                           mode="single" :options="versionsList" :show-no-results="false" placeholder="Версия" />
              <Multiselect v-if="!isRegionalFundEmployee" class="field field--select field--m-width-265 margin-0" v-model="filterRegion"
                           mode="single" :options="regionList" :show-no-results="false" :searchable="true"
                           placeholder="Название региона" :no-options-text="'Укажите регион'" />
            </div>
            <div class="cap-status" v-if="versionStatusText">
              <button type="button" class="cap-history-btn" title="История" @click="showVersionHistory"></button>
              <span class="cap-status__text">Статус версии: {{ versionStatusText }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="content content-white" v-if="loaded">
        <div class="container">
          <div class="table-responsive table-responsive--bg-color-white">
            <table class="table capitalisation">
              <thead>
              <tr>
                <th>Показатель</th>
                <th colspan="4">Значение</th>
              </tr>
              </thead>
              <tbody>
              <tr class="title-row">
                <td>1. КАПИТАЛИЗАЦИЯ</td>
                <td colspan="4">По состоянию на {{ versionData.capitalization?.year || selectedYear }} год</td>
              </tr>
              <tr class="cap-dates-row">
                <td></td>
                <td>31.03</td>
                <td>30.06</td>
                <td>30.09</td>
                <td>31.12</td>
              </tr>
              <tr v-for="(row, index) in capitalizationRows" :key="index" :class="{
                    'table-first-level': row.level === 1,
                    'table-second-level': row.level === 2,
                    'table-third-level': row.level === 3,
                  }">
                <td class="title-td">{{ row.code }} {{ row.title }}</td>
                <td>
                  <NumberInput v-if="isCapitalizationRowEditable(index)" v-model="row.quarter1" :input-class="capitalizationInputClass(row.code, 'quarter1')" :float-format="2" />
                  <span v-else :class="readonlyValueClass(row.code, 'quarter1')">{{ formatDisplayNumber(capitalizationRowQuarterDisplay(index, 'quarter1')) }}</span>
                </td>
                <td>
                  <NumberInput v-if="isCapitalizationRowEditable(index)" v-model="row.quarter2" :input-class="capitalizationInputClass(row.code, 'quarter2')" :float-format="2" />
                  <span v-else :class="readonlyValueClass(row.code, 'quarter2')">{{ formatDisplayNumber(capitalizationRowQuarterDisplay(index, 'quarter2')) }}</span>
                </td>
                <td>
                  <NumberInput v-if="isCapitalizationRowEditable(index)" v-model="row.quarter3" :input-class="capitalizationInputClass(row.code, 'quarter3')" :float-format="2" />
                  <span v-else :class="readonlyValueClass(row.code, 'quarter3')">{{ formatDisplayNumber(capitalizationRowQuarterDisplay(index, 'quarter3')) }}</span>
                </td>
                <td>
                  <NumberInput v-if="isCapitalizationRowEditable(index)" v-model="row.quarter4" :input-class="capitalizationInputClass(row.code, 'quarter4')" :float-format="2" />
                  <span v-else :class="readonlyValueClass(row.code, 'quarter4')">{{ formatDisplayNumber(capitalizationRowQuarterDisplay(index, 'quarter4')) }}</span>
                </td>
              </tr>
              <tr class="total-row">
                <td>{{ capitalizationTotal.code }} {{ capitalizationTotal.title }}</td>
                <td>
                  <span :class="readonlyTotalValueClass(capitalizationTotal.code, 'quarter1')">{{ formatDisplayNumber(capitalizationTotal.quarter1) }}</span>
                </td>
                <td>
                  <span :class="readonlyTotalValueClass(capitalizationTotal.code, 'quarter2')">{{ formatDisplayNumber(capitalizationTotal.quarter2) }}</span>
                </td>
                <td>
                  <span :class="readonlyTotalValueClass(capitalizationTotal.code, 'quarter3')">{{ formatDisplayNumber(capitalizationTotal.quarter3) }}</span>
                </td>
                <td>
                  <span :class="readonlyTotalValueClass(capitalizationTotal.code, 'quarter4')">{{ formatDisplayNumber(capitalizationTotal.quarter4) }}</span>
                </td>
              </tr>

              <tr class="title-row cap-section-gap">
                <td>2. БЮДЖЕТ на выдачу займов, в т.ч. план на текущий год</td>
                <td colspan="4">{{ selectedYear }} год</td>
              </tr>
              <tr v-for="(row, index) in budgetRows" :key="'b' + index">
                <td class="title-td">{{ row.code }} {{ row.title }}</td>
                <td colspan="4"><NumberInput v-model="row.value" :input-class="budgetInputClass(row.code)" :float-format="2" /></td>
              </tr>
              <tr class="total-row" v-if="budgetTotal.title">
                <td>{{ budgetTotal.title }}</td>
                <td colspan="4">
                  <span :class="readonlyTotalValueClass(budgetTotal.code, 'value')">{{ formatDisplayNumber(budgetTotal.value) }}</span>
                </td>
              </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div class="content content-white cap-empty-state" v-else-if="versionsLoaded && !selectedVersion">
        <div class="container">
          <p class="cap-empty-state__text">{{ emptyStateMessage }}</p>
        </div>
      </div>

      <div class="cap-bottom-bar" v-if="loaded">
        <div class="container cap-bottom-bar__inner">
          <button type="button" class="button button--clear" @click="cancelEdit">Отменить</button>
          <button type="button" class="button" :disabled="saving" @click="saveVersion">Сохранить</button>
        </div>
      </div>

      <div class="modal-overlay" id="version-history">
        <div class="popup popup-oy-auto">
          <div class="modal-title title-h3">История изменения версии</div>
          <div class="modal-body">
            <p v-if="historyLoading" class="cap-history-empty">Загрузка...</p>
            <p v-else-if="versionHistory.length === 0" class="cap-history-empty">Нет записей в истории</p>
            <div v-else class="cap-history-list">
              <div v-for="(item, index) in versionHistory" :key="index" class="file-report-history">
                <div class="line">
                  <div class="value">{{ historyStatusName(item.newStatus) }}</div>
                  <div>{{ item.person?.shortName }}, {{ moment(item.timestamp).format('DD.MM.YYYY HH:mm') }}</div>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-bottom">
            <div class="buttons-block">
              <button type="button" class="button button--clear" @click="Shared.closeModal('#version-history')">Закрыть</button>
            </div>
          </div>
        </div>
      </div>
    </main>
    <MainFooter></MainFooter>
  </div>
</template>
